import {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {borderRadius, colors, fontSize, fontWeight, lineHeight, spacing} from '@/styles/tokens';
import {COUNT_UP_DURATION_MS, useCountUp} from '@/hooks/useCountUp';

export interface DonutSegment {
    label: string;
    /** Share of the whole, as a percentage. */
    value: number;
    /** Segment fill. Always a `colors.*` token from the caller.  */
    color: string;
}

export interface DonutChartProps {
    segments: DonutSegment[];
    /** Headline figure rendered in the hole, e.g. "75%". */
    centerValue?: string;
    /** Small qualifier under the headline figure. */
    centerLabel?: string;
    /** Outer diameter in px. */
    size?: number;
    /** Ring thickness in px. */
    thickness?: number;
    /** Accessible summary. Falls back to a generated label. */
    ariaLabel?: string;
    /**
     * Trigger count for drawing the arcs in from nothing. 0 is the resting state — the
     * ring is fully drawn — and every increment replays the draw. Leave it out for a
     * ring that never animates; the SVG geometry is identical either way.
     */
    run?: number;
}

// A 2px gap in the surface colour separates touching segments, so adjacent fills
// never blend into one another for a colour-blind or low-contrast reader.
const SEGMENT_GAP = 2;

// Arcs are revealed by growing their dash — the gap after it stays the full
// circumference. Growth is driven off a counted 0→1 progress rather than a CSS
// transition, on the same timing as the figures beside the chart so they land together.

// The ring's geometry never changes on hover. Pushing the hovered arc outward broke the
// ring's silhouette — it read as the chart coming apart rather than as emphasis — and
// dimming the others read as data being lost. So hover only swaps the readout in the hole,
// and the ring itself stays perfectly still.

const wrapSx = {position: 'relative' as const, display: 'inline-flex', flexShrink: 0} as const;

const centerBoxSx = {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center' as const,
    pointerEvents: 'none' as const
} as const;

const centerValueSx = {
    fontSize: {xs: fontSize[36], md: fontSize[46]},
    fontWeight: fontWeight.extrabold,
    color: colors.primary.main,
    lineHeight: lineHeight[40]
} as const;

const centerLabelSx = {
    fontSize: fontSize[13],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[20]
} as const;

// Tooltip. It reads out inside the donut's hole, taking the centre stat's place for as
// long as the pointer is on a segment. Anchoring it to the hovered arc instead would put
// it on top of the very segment it describes, or push it outside the chart box and into
// the rows beside it; the hole is the one spot that always clears both.
// `pointerEvents: none` keeps it out of the hit testing underneath — without it the
// tooltip steals the pointer from the arc and the readout flickers.
const tooltipSx = {
    position: 'absolute' as const,
    inset: 0,
    zIndex: 1,
    flexDirection: 'column' as const,
    justifyContent: 'center',
    pointerEvents: 'none' as const,
    display: 'flex',
    alignItems: 'center',
    gap: spacing[4],
    textAlign: 'center' as const,
    // Kept inside the hole so a long label wraps rather than running out over the ring.
    paddingInline: spacing[24]
} as const;

const tooltipRowSx = {display: 'flex', alignItems: 'center', justifyContent: 'center', gap: spacing[8]} as const;

// A short stroke of the series colour, not a filled box: at tooltip density a swatch
// is data-weight ink doing a label's job.
const tooltipKeySx = {width: 12, height: 2, borderRadius: borderRadius.full, flexShrink: 0} as const;

// Value leads, label follows — the reader already knows which segment they are on and
// wants the number. Both wear text tokens; the key beside them carries identity.
const tooltipValueSx = {
    fontSize: {xs: fontSize[36], md: fontSize[46]},
    fontWeight: fontWeight.extrabold,
    color: colors.primary.main,
    lineHeight: lineHeight[40]
} as const;

const tooltipLabelSx = {
    fontSize: fontSize[13],
    fontWeight: fontWeight.regular,
    color: colors.text.secondary,
    lineHeight: lineHeight[20]
} as const;

/**
 * DonutChart — dependency-free part-to-whole ring drawn as inline SVG, so it is
 * server-rendered (present in the raw HTML) and adds nothing to the bundle.
 *
 * Segments are stroke-dasharray arcs on a single circle. Read it for the shape at
 * a glance only: exact values belong in labels beside it, never in the ring.
 *
 * Hovering an arc reads its value out in the donut's hole. The ring itself does not move. The tooltip only ever *enhances*: every number it shows is also
 * printed in the labelled rows beside the chart, so nothing is reachable by pointer
 * alone — which is also why the arcs are not focus targets. Keyboard and screen
 * reader users read the rows (and this SVG's own summary), not a hover layer.
 */
const DonutChart = ({segments, centerValue, centerLabel, size = 320, thickness = 52, ariaLabel, run}: DonutChartProps) => {
    const [activeLabel, setActiveLabel] = useState<string | null>(null);
    // Undefined `run`, or 0, means the ring rests fully drawn; a bumped `run` replays it.
    const animating = run !== undefined && run > 0;
    const progress = useCountUp(1, 4, animating, COUNT_UP_DURATION_MS, run);
    const shown = animating ? progress : 1;

    const radius = (size - thickness) / 2;
    const circumference = 2 * Math.PI * radius;
    const total = segments.reduce((sum, segment) => sum + segment.value, 0) || 1;

    // Arcs are laid end to end from 12 o'clock; `share` is each one's slice of the ring,
    // so the same arc can be re-measured against whichever radius it is drawn at.
    let start = 0;
    const arcs = segments.map((segment) => {
        const share = segment.value / total;
        const arc = {...segment, share, start};
        start += share;
        return arc;
    });

    const activeArc = arcs.find((arc) => arc.label === activeLabel);
    const summary = ariaLabel ?? segments.map((segment) => `${segment.label} ${segment.value}%`).join(', ');

    return (
        <Box sx={wrapSx}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={summary}>
                <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                    {arcs.map((arc) => {
                        const length = Math.max(arc.share * circumference - SEGMENT_GAP, 0);
                        return (
                            <circle
                                key={arc.label}
                                cx={size / 2}
                                cy={size / 2}
                                r={radius}
                                fill="none"
                                stroke={arc.color}
                                strokeWidth={thickness}
                                strokeDasharray={`${length * shown} ${circumference}`}
                                strokeDashoffset={-arc.start * circumference}
                                style={{cursor: 'pointer'}}
                                onMouseEnter={() => setActiveLabel(arc.label)}
                                onMouseLeave={() => setActiveLabel(null)}
                            />
                        );
                    })}
                </g>
            </svg>

            {activeArc && (
                <Box sx={tooltipSx}>
                    <Typography component="p" className="font-headers" sx={tooltipValueSx}>
                        {activeArc.value}%
                    </Typography>
                    <Box sx={tooltipRowSx}>
                        <Box sx={{...tooltipKeySx, bgcolor: activeArc.color}} />
                        <Typography component="p" className="font-regular-text" sx={tooltipLabelSx}>
                            {activeArc.label}
                        </Typography>
                    </Box>
                </Box>
            )}

            {(centerValue || centerLabel) && !activeArc && (
                <Box sx={centerBoxSx}>
                    {centerValue && (
                        <Typography component="p" className="font-headers" sx={centerValueSx}>
                            {centerValue}
                        </Typography>
                    )}
                    {centerLabel && (
                        <Typography component="p" className="font-regular-text" sx={centerLabelSx}>
                            {centerLabel}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default memo(DonutChart);
